/**
 * Issue, copy and withdraw a case's partner link (Lloyd, Jul 21: "Let's do the per-case links").
 *
 * The link needs no login, so the panel says so plainly rather than leaving staff to assume it is
 * protected — someone will otherwise paste it into a group thread expecting a sign-in prompt.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

interface CaseShareLinkDto {
  token: string;
  createdAt: string;
  createdBy: string | null;
  revokedAt: string | null;
  lastViewedAt: string | null;
  viewCount: number;
}

interface CaseShareLinkPanelProps {
  Ticket_guid: string;
}

function publicUrlFor(token: string): string {
  return `${window.location.origin}/case/${token}`;
}

const CaseShareLinkPanel: React.FC<CaseShareLinkPanelProps> = ({ Ticket_guid }) => {
  const [link, setLink] = useState<CaseShareLinkDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const base = `${process.env.API_HOST || ''}/api/v1/manager/ticket/${Ticket_guid}/share-link`;

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(base, { credentials: 'include' });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'Could not load the link');
      setLink(data.link ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the link');
    } finally {
      setLoading(false);
    }
  }, [base]);

  useEffect(() => {
    load();
  }, [load]);

  const issue = async (): Promise<void> => {
    // Regenerating kills the old URL, so anyone already holding it loses access — worth a beat of
    // thought rather than a single click, since partners keep these in their own systems.
    if (
      link &&
      !window.confirm(
        'Create a new link? The current one will stop working immediately for anyone who has it.',
      )
    ) {
      return;
    }

    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const response = await fetch(base, { method: 'POST', credentials: 'include' });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'Could not create the link');
      setLink(data.link);
      setNotice(link ? 'New link created. The previous one no longer works.' : 'Link created.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the link');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (): Promise<void> => {
    if (!window.confirm('Withdraw this link? Anyone holding it will lose access immediately.')) {
      return;
    }

    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const response = await fetch(base, { method: 'DELETE', credentials: 'include' });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'Could not withdraw the link');
      setLink(null);
      setNotice('Link withdrawn.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not withdraw the link');
    } finally {
      setBusy(false);
    }
  };

  const copy = async (): Promise<void> => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(publicUrlFor(link.token));
      setNotice('Link copied.');
    } catch {
      setError('Could not copy automatically — select the link and copy it manually.');
    }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <LinkIcon sx={{ mr: 1 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Referral partner link
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          A link a referral partner can open without signing in. It shows this case's progress,
          current status and outcome only — no client details and no documents.{' '}
          <strong>Anyone who has the link can view it</strong>, so send it only to the partner
          handling the case.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {notice && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
            {notice}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={22} />
          </Box>
        ) : link ? (
          <Stack spacing={2}>
            <TextField
              value={publicUrlFor(link.token)}
              fullWidth
              size="small"
              InputProps={{ readOnly: true }}
              onFocus={(e) => e.target.select()}
            />
            <Typography variant="caption" color="text.secondary">
              Created {new Date(link.createdAt).toLocaleDateString()}
              {link.createdBy ? ` by ${link.createdBy}` : ''} ·{' '}
              {link.viewCount === 0
                ? 'not opened yet'
                : `opened ${link.viewCount} time${link.viewCount === 1 ? '' : 's'}${
                    link.lastViewedAt
                      ? `, last on ${new Date(link.lastViewedAt).toLocaleDateString()}`
                      : ''
                  }`}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button size="small" variant="contained" startIcon={<ContentCopyIcon />} onClick={copy}>
                Copy link
              </Button>
              <Button size="small" onClick={issue} disabled={busy}>
                Create new link
              </Button>
              <Button size="small" color="error" onClick={revoke} disabled={busy}>
                Withdraw
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Button variant="contained" onClick={issue} disabled={busy} startIcon={<LinkIcon />}>
            {busy ? 'Creating…' : 'Create partner link'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default CaseShareLinkPanel;
